// ─────────────────────────────────────────────
//  TimelineSidePanels — columns 5-7 of the Timeline Chart page (585px).
//
//  ONE GRID, ordered by the question a clinician actually asks, in the order
//  they ask it — reading rows left to right:
//
//    NEEDS YOU NOW  │ MD CALL LIST     │ FREE NOW      is anyone in trouble,
//                                                      who do I ring, who is free?
//    BREAKS         │ BOUNDARY OUTLOOK │ MAINTENANCE   what is being worked, and
//                                                      what is the day heading into?
//    ORIENTEES      │ TASK DIRECTIVES (2 tracks)      who the bars do not show,
//                                                      and the board's own outbox
//    LOCUM LIST (full width)                           set it and forget it
//
//  ONE RANKED LIST, NOT FOUR EQUAL CARDS. Four cards of equal size assert that an
//  unacknowledged emergency and an even boundary are the same size problem. Rank
//  is carried by the tier word AND the rail thickness AND the type size, so it
//  survives greyscale and a colour-blind reader.
//
//  Purely presentational — every value arrives as a prop.
// ─────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from "react";
import tokens from "../../theme/tokens";
import TimelineStatusBand from "./TimelineStatusBand";
// The card shell every box here is drawn in. Its own module so a card the
// PAGE builds (see `finishedRoomsCard`) can import it without dragging this
// file's whole graph — down to firebase — along with it.
import Card from "./PanelCard";
import TimelineBreaksCard from "./TimelineBreaksCard";
// ⚠️ IMPORT **AND** RE-EXPORT — FreeNow below CALLS both of these, and a bare
// `export … from` creates no local binding (the smartName ReferenceError).
import { freeAtLabel, elapsed, elapsedLabel } from "./freeSince";
// The board's ONE minutes-to-clock formatter. A local copy here would print a
// different "3 PM" than the bars and the ORIENTEES card do.
import { to12h } from "../../domain/timeline";
import { fixModeFor } from "./OutstandingFixModal";
import { onCallChipLabel, onCallChipTitle } from "../../domain/onCallChip";

export { freeAtLabel, elapsed, elapsedLabel };

const { color, font, radius } = tokens;

// Severity 0-2 are alarms; 3+ are things to plan. `outstandingItems` already
// returns them pre-sorted by severity, so this only maps a rank to a treatment.
function tierOf(severity) {
  if (severity <= 1) return { word: "ALARM", ink: "#991b1b", bg: "#fef2f2", rail: 5, size: 15 };
  if (severity === 2) return { word: "ACT NOW", ink: "#7a4f00", bg: "#fef8ec", rail: 4, size: 13.5 };
  return { word: "PLAN", ink: "#1e3a8a", bg: color.surface.card, rail: 3, size: 12.5 };
}

// ── HOW MANY COLUMNS ────────────────────────────────────────────────────────
// ⚠️ NOT `auto-fit`, AND THAT IS THE WHOLE POINT. `repeat(auto-fit, minmax(280px,
// 1fr))` fits as many tracks as it can, which is not the same question as "how
// many does this layout want". Measured: the panel is 1184px wide when it runs
// under the chart, and auto-fit put FOUR cards across it — BREAKS climbed onto
// row 1 beside FREE NOW and the row structure below stopped meaning anything.
// Three is the most this panel ever wants, so three is the cap.
const CARD_MIN = 280; // the narrowest a card in here still reads
const GRID_GAP = 12;
// ⚠️ HYSTERESIS, AND IT IS NOT THEORETICAL. Flipping the column count changes
// how TALL the panel is, and the page's main region is `overflowY: auto` — so a
// flip can add or remove a vertical scrollbar, which changes the width of every
// descendant by the scrollbar's width. Sitting exactly on a threshold, that is
// a loop: 3 columns → shorter → scrollbar goes → wider → still 3 → taller →
// scrollbar returns. A band means a width has to commit before the count moves.
const BAND = 8;
const floorFor = (n) => (n === 3 ? 3 * CARD_MIN + 2 * GRID_GAP : n === 2 ? 2 * CARD_MIN + GRID_GAP : 0);

// Pure, and exported so the thresholds are tested without a layout engine.
// `prev` is optional: called with one argument it is the plain threshold
// function it always was, which is how the boundary tests read.
export function columnsForWidth(width, prev = null) {
  // Unmeasured (first paint, and jsdom, which lays nothing out) → the board's
  // home, a desktop. Guessing 1 here would flash a single stacked column on
  // every load of the page this panel is mostly used on.
  if (!width) return 3;
  const next = width >= floorFor(3) ? 3 : width >= floorFor(2) ? 2 : 1;
  if (prev == null || next === prev) return next;
  // Dropping a column needs the width to be a clear band BELOW the floor it is
  // leaving; gaining one needs it a clear band above the floor it is entering.
  return next < prev
    ? (width <= floorFor(prev) - BAND ? next : prev)
    : (width >= floorFor(next) + BAND ? next : prev);
}

// ⚠️ A CALLBACK REF, NOT `useEffect(…, [])` — the same scar useTrackGeometry
// carries, and for the same reason: the node this measures can be behind a
// branch that has not rendered yet, and an effect keyed on `[]` would run once,
// find nothing, and measure nothing, silently and forever.
//
// It measures THE PANEL, not the window. Those are different numbers: at a
// 1600px window this grid is 646px (it sits in the page's 3-of-7 side rail),
// and at a 1120px window it is 1184px (the rail has collapsed and it runs full
// width). A window-width breakpoint gets that exactly backwards.
function usePanelColumns() {
  const [columns, setColumns] = useState(() => columnsForWidth(0));
  const observerRef = useRef(null);

  // ⚠️ THIS REF GOES ON A WRAPPER, NEVER ON THE GRID IT SIZES. Measuring the
  // element whose `gridTemplateColumns` you are about to set is a feedback
  // loop waiting for a style edit: today the grid's width is handed down by the
  // page's 7-column layout, so choosing 1, 2 or 3 tracks cannot change it — but
  // that is true only while the stack above stays stretched and its ancestors
  // keep `minWidth: 0`. Someone adding `alignItems: "flex-start"` to stop a card
  // stretching would quietly make this box shrink-to-fit, and then its width
  // WOULD be a function of the count it just chose. Measuring the outer stack
  // costs nothing and cannot be broken from the inside.
  const gridRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;
    const read = () => {
      const w = node.getBoundingClientRect().width;
      setColumns((prev) => columnsForWidth(w, prev));
    };
    read();
    if (typeof ResizeObserver === "undefined") return; // jsdom
    const ro = new ResizeObserver(read);
    ro.observe(node);
    observerRef.current = ro;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { gridRef, columns };
}

// ── 1. NEEDS YOU NOW ────────────────────────────────────────────────────────
function NeedsYouNow({ items, unavailable, checkedAtLabel, canFix, onFix }) {
  if (unavailable) {
    return (
      <Card title="NEEDS YOU NOW" note="not available here" testId="needs-you-now">
        {/* NOT the all-clear empty state. "Nothing outstanding" is an assertion
            of safety, and on a campus whose alerts we are not receiving it would
            be a lie. */}
        <div style={s.unavailable}>
          <strong style={s.unavailableTitle}>Alerts not available for this facility</strong>
          <p style={s.unavailableBody}>
            Emergencies, uncovered rooms and break requests follow your signed-in
            campus. This page is showing a different one, so this list cannot be
            trusted and is not shown.
          </p>
        </div>
      </Card>
    );
  }
  return (
    <Card
      title="NEEDS YOU NOW"
      count={items.length}
      note="ranked · not sorted by age"
      testId="needs-you-now"
    >
      {items.length === 0 ? (
        // An empty card and a broken card look identical, so this one names what
        // it is watching and stamps when it last knew.
        <div style={s.allClear}>
          <strong style={s.allClearTitle}>✓ Nothing outstanding{checkedAtLabel ? ` — as of ${checkedAtLabel}` : ""}</strong>
          <div style={s.allClearLabel}>QUIETLY WATCHING</div>
          <ul style={s.allClearList}>
            <li>emergency alerts and acknowledgements</li>
            <li>rooms running without a provider</li>
            <li>cover lapsing before a case ends</li>
            <li>breaks requested and waiting</li>
          </ul>
        </div>
      ) : (
        <div style={s.rows}>
          {items.map((item) => {
            const t = tierOf(item.severity);
            // ⚠️ ONLY ITEMS WITH SOMEWHERE TO GO. `fixModeFor` returns null for
            // an unacknowledged emergency (it needs an acknowledgement, not a
            // placement) and for the forecast rows (nothing to fill yet). A
            // button opening a screen that offers nothing is worse than no
            // button — it teaches people the button does not work.
            //
            // An item may ALSO veto its own button with `fixable: false`. The
            // uncovered item does exactly that when the room it names has no
            // case row on the chart to rewrite — an OB room sitting on a
            // standing on-call watch draws no bar, so the fix screen would
            // build zero plans.
            const fixable =
              canFix && onFix && item.fixable !== false && Boolean(fixModeFor(item));
            return (
              <div
                key={item.key}
                style={{ ...s.row, background: t.bg, borderLeftWidth: t.rail, borderLeftColor: t.ink }}
                data-severity={item.severity}
              >
                <span style={{ ...s.tier, color: t.ink, borderColor: t.ink }}>{t.word}</span>
                <div style={s.rowBody}>
                  <div style={{ ...s.rowTitle, fontSize: t.size }}>{item.label}</div>
                  {item.rooms?.length > 0 && (
                    <div style={s.rowRooms}>{item.rooms.join(" · ")}</div>
                  )}
                </div>
                {/* OUTLINED, NOT FILLED, AND SHORTER THAN THE TITLE LINE.
                    These rows are RANKED and the ranking is the safety
                    property, so the button may not change a row's height or its
                    order. It also may not add a fifth saturated colour to a card
                    whose reds already mean somebody is in trouble — an outline
                    in the app's navy reads as "an action" without competing. */}
                {fixable && (
                  <button
                    type="button"
                    style={s.fixBtn}
                    onClick={() => onFix(item)}
                    aria-label={`Fix — ${item.label}`}
                  >
                    Fix
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── ORIENTEES ───────────────────────────────────────────────────────────────
//
// Owner, 2026-08-24: *"I want to create a box in the time line page below the
// breaks box. Will be name orientees… place the orientis like batz and briana…
// removed them from the time line and… keep in this new box. Add information of
// the room and at what time the leave."*
//
// ⚠️ THIS CARD IS WHERE A TRAINEE IS VISIBLE NOW, AND NOWHERE ELSE ON THE CHART.
// The bars stopped drawing a second band the same day (see `barCase` in
// TimelineChartPage), so if this card is empty when a trainee is on the board,
// that person has disappeared from the screen entirely — which is exactly the
// failure the two-CRNA work existed to end. Its emptiness is load-bearing.
//
// ⚠️ AND THEY ARE STILL COUNTED. Orientees remain on the daily roster and in the
// staffing maths ("yes they are part of daily roster", same day). This card
// REPORTS them; it does not decide whether they cover anything.
function Orientees({ rows = [], onMovePreceptorOut = null, onSwapPair = null, onEditPair = null }) {
  return (
    <Card title="ORIENTEES" count={rows.length} note="on the board, not on a bar" testId="orientees-card">
      {rows.length === 0 ? (
        <div style={s.emptyLine}>Nobody is orienting today.</div>
      ) : (
        <div style={s.people}>
          {rows.map((r) => {
            // ⚠️ ONE REFUSAL PER ABSENCE. A HELPING pair is two colleagues
            // sharing a room, not a teaching relationship — there is no preceptor
            // to step out of it, so the control was never on offer and a greyed
            // button with an explanation would be noise. An UNMATCHED trainee is
            // the opposite: the control is meant to be there, it is withheld,
            // and a human has to go and fix the name. That one says so.
            // ⚠️ BOTH CONTROLS ARE ALWAYS DRAWN NOW. They used to be hidden when
            // refused, on the rule that explaining a control nobody was offered is
            // noise. The owner then reported the SAME confusion twice on two
            // different rows — "i dont see the swap button", "i dont see the
            // preceptor free now button" — which is the rule failing its own test:
            // an absent control looks exactly like a broken one, and the reader
            // cannot tell "not applicable here" from "this screen is wrong". A
            // withheld control that says why is legible; a missing one is not.
            const moveReason = r.canMoveOut ? "" : String(r.blockedReason || "");
            const swapReason = r.canSwap ? "" : String(r.swapBlockedReason || "");
            // ⚠️ ONE SENTENCE WHEN IT IS ONE FACT. An unmatched trainee refuses
            // BOTH controls for the identical reason, and printing it twice under
            // one row is the noise the old rule was right to fear. Deduped, with
            // both controls pointing at the single sentence.
            const reasons = [...new Set([moveReason, swapReason].filter(Boolean))];
            // ⚠️ SLUGIFIED, BECAUSE `aria-describedby` IS A SPACE-SEPARATED ID
            // LIST. `r.key` is `${sp.key}::${room}`, and the reason only renders
            // when the trainee is UNMATCHED — which is exactly when `sp.key` is
            // the synthetic `sp:<name>` and carries a space ("sp:kayla gibbons"),
            // with a room like "WOR 5" adding another. The raw key resolved to
            // four bogus references, so the refusal was announced to nobody.
            const slug = String(r.key).replace(/[^A-Za-z0-9_-]+/g, "-");
            // Returns undefined for a reason that is not on the row, so a control
            // never carries an `aria-describedby` pointing at nothing — a dangling
            // reference is silently ignored by AT and reads as "no explanation".
            const reasonId = (text) =>
              text && reasons.includes(text)
                ? `orientee-blocked-${slug}-${reasons.indexOf(text)}`
                : undefined;
            return (
              <div key={r.key} style={s.orienteeRow}>
                <div style={s.orienteeTop}>
                  <span style={s.avatar}>{initials(r.name)}</span>
                  {/* ⚠️ THE NAME IS A CONTROL, AND ITS OWN CONTROL. Owner
                      asked to change the people "directly fron the box"; this
                      opens the ROOM'S EXISTING EDITOR rather than growing a
                      third way to write a seat, because that editor also
                      disposes whoever leaves and stamps them free. Rendered as
                      plain text for a viewer who may not edit, so nothing on
                      screen offers a write that cannot happen. */}
                  {onEditPair ? (
                    <button
                      type="button"
                      style={s.orienteeNameBtn}
                      onClick={() => onEditPair(r)}
                      aria-label={`${r.name}, the second provider — change who is in ${r.room || "the room"}`}
                    >
                      {r.name}
                    </button>
                  ) : (
                    <span style={s.orienteeName}>{r.name}</span>
                  )}
                </div>
                {/* ⚠️ THE ROOM AND THE HOUR GET THEIR OWN LINE, SO THE NAME KEEPS
                    THE FIRST ONE. They are `whiteSpace: nowrap` and the name was
                    not, so on one flex row the name was the only thing that could
                    give way — "Joshua Larson" rendered as "Joshua Larso…" on a card
                    whose entire job is WHO. The FREE NOW card below carries the
                    same scar and the same fix. */}
                <div style={s.orienteeFacts}>
                  {/* Room first, then the hour: "where are they" is the question a
                      charge nurse asks before "when do they go". */}
                  <span style={s.orienteeRoom}>{r.room || "no room"}</span>
                  {/* ⚠️ NO INVENTED HOUR. A trainee whose leaving time nobody has
                      recorded says so, rather than borrowing the seat holder's —
                      two people in one room leave at two different times, which is
                      the whole reason this data exists. */}
                  {/* ⚠️ THREE STATES, NOT TWO. A stated hour, no hour at all,
                      and — since the importer began borrowing the preceptor's
                      shift for a trainee the sheet gave no number — an ASSUMED
                      hour. It is printed, because it is what the room will run
                      on, and it is marked, because a charge nurse deciding
                      whether they lose a person at 3pm needs to know nobody
                      actually said so. */}
                  <span style={s.orienteeLeave}>
                    {r.departLabel
                      ? `leaves ${r.departLabel}${r.departAssumed ? " (assumed)" : ""}`
                      : "no leaving time"}
                  </span>
                </div>
                {/* ⚠️ THE SECOND LINE EXISTS SO THE NAME SURVIVES. Anything added
                    to a one-line flex row comes out of the only shrinkable thing
                    in it, which is the person's name. The preceptor and the
                    control take their own line rather than truncating the
                    trainee. */}
                <div style={s.orienteeMeta}>
                  {/* ⚠️ A SIBLING OF THE TRAINEE'S CONTROL, NEVER NESTED IN IT.
                      The bar carried exactly that bug: the trainee's chip sat
                      inside the seat holder's button, so her name opened his
                      menu and she could not be moved at all. */}
                  <span style={s.orienteeWith}>
                    with{" "}
                    {onEditPair ? (
                      <button
                        type="button"
                        style={s.orienteeWithBtn}
                        onClick={() => onEditPair(r)}
                        aria-label={`${r.preceptorName || "the seat holder"}, holding the room — change who is in ${r.room || "the room"}`}
                      >
                        {r.preceptorName || "unassigned"}
                      </button>
                    ) : (
                      <strong style={s.orienteeWithName}>{r.preceptorName || "—"}</strong>
                    )}
                  </span>
                  {/* ⚠️ THE REASON STAYS OUTSIDE THE BUTTON GROUP. It is a
                      sentence, the group cannot shrink, and putting the two
                      together made the whole thing rigid — the refusal text
                      pushed "Preceptor → FREE NOW" clean off the right edge of
                      the card. Text shrinks, controls do not. */}
                  {reasons.map((text) => (
                    <span key={text} id={reasonId(text)} style={s.orienteeBlocked}>
                      {text}
                    </span>
                  ))}
                  <div style={s.orienteeActions}>
                    {/* ⚠️ THE SWAP IS GATED SEPARATELY, AND ON PURPOSE. It needs a
                        resolved second person and an occupied seat; it does NOT need
                        the pair to be an orientation, because two colleagues sharing
                        a room can still be recorded the wrong way round. So a helping
                        pair whose move-out is refused still gets a working swap —
                        and now says so instead of showing nothing. */}
                    {onSwapPair && (
                      <button
                        type="button"
                        style={r.canSwap ? s.orienteeBtn : s.orienteeBtnOff}
                        aria-disabled={r.canSwap ? undefined : true}
                        aria-describedby={r.canSwap ? undefined : reasonId(swapReason)}
                        onClick={r.canSwap ? () => onSwapPair(r) : undefined}
                        aria-label={`Swap: ${r.name} holds ${r.room || "the room"}, ${r.preceptorName || "the other"} becomes the second provider`}
                      >
                        &#8645; SWAP
                      </button>
                    )}
                    {/* Named for the CONSEQUENCE, not the gesture. "Move out"
                        says nothing about where the person lands or who is left
                        holding the room, and two different actions must not
                        share one verb. */}
                    {onMovePreceptorOut && (
                    /* ⚠️ `aria-disabled`, NOT `disabled`. A disabled button is out
                        of the tab order, and `aria-describedby` is announced ON
                        FOCUS — so the refusal printed above it was reaching a
                        browse-mode reader only by luck and a focus-mode one never.
                        Kept focusable with an inert onClick, the reason is announced
                        to whoever tabs onto it.
                        ⚠️ THE VISIBLE WORDS COME FIRST in the accessible name (SC
                        2.5.3 Label in Name). It used to omit "FREE NOW" entirely, so
                        somebody using speech input could say exactly what the button
                        says and match nothing. The arrow is a symbol, not a word, and
                        is correctly left out.
                        ⚠️ AND `preceptorName` NEEDS ITS FALLBACK. It is "" for a row
                        whose seat is empty — precisely the `fixable` row this button
                        renders withheld on — so the name read "Preceptor  leaves
                        WOR 6" with the subject missing. */
                      <button
                        type="button"
                        style={r.canMoveOut ? s.orienteeBtn : s.orienteeBtnOff}
                        aria-disabled={r.canMoveOut ? undefined : true}
                        aria-describedby={r.canMoveOut ? undefined : reasonId(moveReason)}
                        onClick={r.canMoveOut ? () => onMovePreceptorOut(r) : undefined}
                        aria-label={`Preceptor FREE NOW: ${r.preceptorName || "the seat holder"} leaves ${r.room || "the room"}, ${r.name} takes the room`}
                      >
                        Preceptor &rarr; FREE NOW
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── 2. FREE NOW ─────────────────────────────────────────────────────────────
function FreeNow({ providers, doctors, freeSince = {}, nowMs, nowMin = null, canAssign, onAssign, renderSendHome = null }) {
  const total = providers.length + doctors.length;
  // ⚠️ PAST THEIR HOUR IS NOT GONE. Owner, 2026-09-10: somebody due out at 3 PM
  // is still on this list at 3:15. They sink to the bottom under their own
  // heading and are offered a way home — they are NOT removed and NOT marked
  // departed, because a scheduled end is not evidence that anybody left, and
  // writing one from the other under-counts every CRNA who stays late.
  const onTime = providers.filter((p) => !p.dueOut);
  const dueOut = providers.filter((p) => p.dueOut);
  // ⚠️ THE HEADER COUNTS THE WHOLE BENCH. The note says "same list the rail
  // counts", and these people are still on the roster until somebody sends them
  // home — counting only the on-time ones would make this card disagree with the
  // rail beside it. The group carries its own count instead.

  // ONE row, rendered in two places — the on-time list and the DUE OUT group.
  // A helper rather than a second copy of the markup: the last time this card
  // grew a second spelling of a row, the two disagreed about which line the name
  // sat on.
  const providerRow = (p) => {
    const since = freeSince[p.id];
    const dur = elapsed(since, nowMs);
    const overdueBy = p.dueOut && nowMin != null && p.departMin != null
      ? elapsedLabel(nowMin - p.departMin)
      : "";
    return (
      <React.Fragment key={p.id}>
              <div style={p.dueOut ? s.personDueOut : s.person}>
                <span style={s.avatar}>{initials(p.name)}</span>
                {/* ⚠️ THE NAME GETS ITS OWN LINE, and that is the whole point of
                    this card. It used to sit on ONE flex line with the avatar,
                    the chips, the free-since time, the role and the Assign
                    button — and it was the only shrinkable thing on that line,
                    so it lost every width fight: at the panel's real width the
                    bench read "Doris Velasq…", "Yarsellis Fro…" and, behind an
                    OB CALL chip, "M…". A card whose job is to tell you WHO is
                    free cannot be the card that truncates the who. Everything
                    secondary drops to a second line underneath. */}
                <div style={s.personBody}>
                  {/* ⚠️ THE HOUR SITS BESIDE THE NAME, and it is ABSENT when nobody
                      recorded one — never a guess and never a dash pretending to be
                      one. Owner, 2026-09-10: "add the departure time in right side of
                      the name". `departMin` comes from `benchProviders`, which asks
                      the one departure helper, so an on-call CRNA reads 7 AM here
                      rather than looking overdue. */}
                  <span style={s.personNameRow}>
                    <span style={s.personName}>{p.name}</span>
                    {p.departMin != null && (
                      <span
                        style={p.dueOut ? s.personDepartOut : s.personDepart}
                        title={p.dueOut ? "Past the end of their shift" : "When they are due to leave"}
                      >
                        {to12h(p.departMin)}
                      </span>
                    )}
                  </span>
                  <span style={s.personSub}>
                {/* OB CALL — A QUIET CHIP, NOT AN ALARM. This person is on the
                    bench for a REASON: they hold the standing on-call watch and
                    are free between epidurals and sections. That is a fact worth
                    reading before you send them somewhere, but it is not an
                    emergency, and red on this card belongs to the rooms that
                    are. `onCall` rides in on the bench entry
                    (`benchProviders`) — the same flag the chart uses to decide
                    it draws no bar — so this never re-derives it. */}
                {/* No chip at all when the watch is not OB — `onCallChipLabel`
                    returns null and the person reads as plainly free, which is
                    what they are. */}
                {p.onCall && onCallChipLabel(p.onCallRoom) && (
                  <span style={s.onCallChip} title={onCallChipTitle(p.onCallRoom)}>
                    {onCallChipLabel(p.onCallRoom)}
                  </span>
                )}
                {/* GIVING BREAKS — why this free person is not spare. They are
                    walking the break rounds, so they stay on this list and stay
                    callable, but sending them to a room takes the break-giver
                    off the floor. Amber is the board's break colour everywhere
                    else (the chips on the BREAKS card, the ☕ on a room card);
                    outline only, never a fill and never red, because this is a
                    duty and not an alarm. */}
                {p.givingBreaks && (
                  <span style={s.givingBreaksChip} title="Giving breaks — free, but covering the break rounds">
                    GIVING BREAKS
                  </span>
                )}
                {/* THE TIMER RUNS FROM A RECORDED MOMENT, NOT FROM FIRST SIGHT.
                    It counts from the stamp written when the provider actually
                    came out of a room. A provider who was never assigned today
                    has no stamp and reads TBD — the board says "we did not see
                    you leave a room" rather than inventing a start, which is
                    what counting from page-load would have done (and would have
                    reset on every refresh and disagreed between devices). */}
                <span style={s.personMeta}>{p.subRole || p.role || "provider"}</span>
                {/* ⚠️ THE HOUR AND HOW LONG AGO, because "due out" alone does not
                    say whether they went ten minutes ago or three hours ago —
                    and that is the difference between a nudge and a person the
                    board has been wrong about all afternoon. */}
                {/* The HOUR is on the name line now; this says how far past it we
                    are, which is the difference between a nudge and somebody the
                    board has been wrong about all afternoon. */}
                {p.dueOut && overdueBy && (
                  <span style={s.dueOutTag} title="Past the end of their shift. Nobody has recorded them leaving.">
                    overdue {overdueBy}
                  </span>
                )}
                {since ? (
                  <span style={s.freeBlock}>
                    <span style={s.freeAt} title="Time they came out of a room">
                      {freeAtLabel(since)}
                    </span>
                    {/* Secondary, and it is allowed to be absent: the clock time
                        alone still answers the question if the tick is missing. */}
                    {dur && <span style={s.freeFor}>{dur}</span>}
                  </span>
                ) : (
                  /* TBD is To Be Decided — a STAND-BY state, not a gap in the
                     data. The person is here and unassigned, and they move to
                     FREE NOW. Saying "no start time to count from" describes
                     why there is no timer, which is the mechanism, not the
                     meaning; a reader needs to know these are the people
                     available to be placed.
                     `title` is also a mouse affordance and never reaches a
                     keyboard or a screen reader, so the meaning rides along in
                     text as well. */
                  <span style={s.tbd} title="To be decided — on stand-by, not yet assigned to a room. Moves to FREE NOW.">
                    TBD
                    <span style={s.srOnly}> — to be decided: on stand-by, not yet assigned to a room, and available to be placed. Moves to FREE NOW.</span>
                  </span>
                )}
                  </span>
                </div>
                {/* ⚠️ STILL ASSIGNABLE. Past their hour is not gone, and a room
                    that needs covering at 3:20 may well be covered by the person
                    who was due out at 3 — the AIC decides, not the clock. The
                    action offered CHANGES; nothing is taken away. */}
                {p.dueOut
                  ? renderSendHome && renderSendHome(p)
                  : canAssign && onAssign && (
                      <button type="button" style={s.assignBtn} onClick={() => onAssign(p)}>
                        Assign →
                      </button>
                    )}
              </div>
      </React.Fragment>
    );
  };

  return (
    <Card title="FREE NOW" count={total} note="same list the rail counts" testId="free-now">
      {total === 0 ? (
        <div style={s.emptyLine}>Nobody on the bench — everyone on shift is in a room.</div>
      ) : (
        <div style={s.people}>
          {onTime.map(providerRow)}
          {doctors.map((d) => (
            <div key={d.id} style={s.person}>
              <span style={{ ...s.avatar, background: "#e8edf9", color: "#1e3a8a" }}>{initials(d.name)}</span>
              <div style={s.personBody}>
                <span style={s.personName}>{d.name}</span>
                <span style={s.personSub}>
                  <span style={s.personMeta}>supervising no room</span>
                </span>
              </div>
            </div>
          ))}
          {/* ⚠️ LAST IN THE CARD, AFTER THE DOCTORS. Rendered between the two
              lists, the heading swallowed the doctor beneath it — "Anna Lipska,
              supervising no room" read as somebody due out at 3 PM. A group
              heading owns everything below it until the card ends, so it has to
              come last. */}
          {dueOut.length > 0 && (
            <>
              <div style={s.dueOutHead}>
                DUE OUT · {dueOut.length}
                <span style={s.dueOutNote}>past their leaving time, still on the bench</span>
              </div>
              {dueOut.map(providerRow)}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// ── 3. BOUNDARY OUTLOOK ─────────────────────────────────────────────────────
//
// The next shift change measured against the rooms ACTUALLY RUNNING — it moves
// with add-ons and early finishes, and it is what predicts tonight's uncovered
// room.
//
// The coverage map is deliberately NOT shown here. It answers a different
// question (are we staffed to the number this campus is meant to cover?) and it
// has exactly one home: the Staffing Adequacy section at the foot of the page.
// Two staffing verdicts side by side, measured against different denominators,
// would read as one contradictory judgement.
function BoundaryOutlook({ boundary, facilityLabel, weekdayLabel }) {
  const short = boundary && boundary.delta < 0 ? Math.abs(boundary.delta) : 0;
  return (
    <Card
      title="BOUNDARY OUTLOOK"
      note={[facilityLabel, weekdayLabel].filter(Boolean).join(" · ")}
      testId="boundary-outlook"
    >
      <div style={s.splitRow}>
        <div style={s.splitLabel}>OPERATIONAL<br />SAFETY</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {boundary ? (
            <>
              <div style={{ ...s.staffChip, ...(short ? s.staffChipShort : s.staffChipOk) }}>
                {short
                  ? `SHORT ${short} FOR ROOMS RUNNING AT ${boundary.label}`
                  : `OK AT ${boundary.label} FOR ROOMS RUNNING`}
              </div>
              <div style={s.splitNote}>
                {boundary.crnas} staying past {boundary.label} for {boundary.rooms} rooms still running.
              </div>
            </>
          ) : (
            <div style={s.emptyLine}>No boundary ahead today.</div>
          )}
        </div>
      </div>

    </Card>
  );
}

// ── 4. MD CALL ──────────────────────────────────────────────────────────────
function MdCall({ rows, departed = {}, renderDepart }) {
  return (
    <Card title="MD CALL LIST" note="reference · not an alarm" testId="md-call">
      {rows.length === 0 ? (
        <div style={s.emptyLine}>No MD call list on today's board.</div>
      ) : (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, ...s.thTight }} scope="col">Position</th>
              <th style={s.th} scope="col">Doctor</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isDeparted = Boolean(departed[r.id]);
              const depart = renderDepart && !isDeparted ? renderDepart(r) : null;
              return (
              <tr key={r.id}>
                <td style={{ ...s.td, ...s.thTight }}>
                  {/* "L" ON THE CALL LIST MEANS LOCUM, NEVER "LAST". The sheet
                      marks a locum by giving them NO call number. Spelled out in
                      full here because "#L" reads as a call position, which is
                      precisely what a locum does not have. There is no "Late"
                      designation — ruled out 2026-08-07. */}
                  {r.isLocum
                    ? <span style={s.locumPill}>Locum</span>
                    : <span style={s.callNum}>{r.callOrder ?? "—"}</span>}
                </td>
                <td style={s.td}>
                  <div style={s.mdRow}>
                    {/* A departed doctor STAYS ON THE LIST, struck through. The
                        board's job is to say who is still reachable; silently
                        dropping the row would leave a reader wondering whether
                        they were ever on call. Same treatment AvailabilitySection
                        uses on the Daily List. */}
                    <span style={isDeparted ? s.mdNameDeparted : undefined}>{r.name}</span>
                    {isDeparted && <span style={s.departedTag}>departed</span>}
                    {r.leaveLabel && <span style={s.leaveTag}>leaves {r.leaveLabel}</span>}
                    {depart && <span style={s.mdAction}>{depart}</span>}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

export default function TimelineSidePanels({
  outstanding = [],
  alertsCovered = true,
  checkedAtLabel,
  available,
  boundary,
  facilityLabel,
  weekdayLabel,
  mdCall = [],
  departedDoctors,
  renderDepart,
  travelingBox,
  // The board→phone composer, handed in as a NODE so this file places it and
  // TimelineChartPage keeps owning its data — same contract as `locumList` and
  // `travelingBox`. Omitted, the row renders exactly as it did before.
  taskCard = null,
  // ⚠️ THE FINISHED-ROOM WARNING, ALSO A NODE. It asks about rooms whose clock
  // and whose status disagree, and pressing either of its buttons WRITES — so
  // the page owns it and this file only places it, exactly as with `taskCard`.
  // It renders null when no room is in doubt, which is most of every day.
  finishedRoomsCard = null,
  // MAINTENANCE — the two board-setup launchers, also handed in as a NODE so
  // the page keeps owning the modals they open. Omitted, this row renders
  // exactly as it did before (see the wrapper note at its placement below).
  maintenanceCard = null,
  locumList,
  freeSince = {},
  nowMs,
  canAssign,
  onAssign,
  status,
  // Sounding the all-clear on an emergency. Passed straight through to the band;
  // this file decides nothing about who may call it.
  onClearEmergency = null,
  // Confirming a staged handover. Passed straight through to the band; this
  // file decides nothing about who may call it.
  onConfirmRelief = null,
  // Naming a break cover from the BREAKS card. Straight through; this file
  // decides nothing about who may call it.
  onAssignBreak = null,
  onCancelRequest = null,
  busy = false,
  breakRows = [],
  orienteeRows = [],
  // The move-out control on an ORIENTEES row. `onMovePreceptorOut(row)` hands the
  // whole row back — including `assignmentId` — so the write re-derives its patch
  // from the LIVE assignment rather than trusting this render's copy.
  onMovePreceptorOut = null,
  // The swap control on an ORIENTEES row. Same contract as the move-out: the
  // whole row goes back so the write re-derives its patch from the LIVE
  // assignment. A DIFFERENT action though - nobody leaves the room - so it is a
  // separate handler rather than a flag on the one above.
  onSwapPair = null,
  // Minute-of-day for the board's clock, so FREE NOW can say how long ago
  // somebody was due out. Separate from `nowMs`, which drives the free-since
  // timer: one is a wall clock, the other a stopwatch.
  nowMin = null,
  // ⚠️ A RENDER PROP, LIKE `renderDepart` ON THE MD RAIL. Sending somebody home
  // is the full DepartureFlow — it reassigns their rooms and can require an AIC
  // handoff — and that component owns a modal and its own writes. This file is
  // presentational, so the PAGE supplies the element and this card only decides
  // where it sits.
  renderSendHome = null,
  // Clicking either NAME on an ORIENTEES row. Opens the room's own editor, which
  // already replaces the seat and the second provider AND does the parts a card
  // cannot see - disposing whoever left and stamping them free.
  onEditPair = null,
  onOpenRoom,
  // The Fix affordance on a NEEDS YOU NOW row. `onFix(item)` hands the whole
  // outstanding item back — including `item.conflict` — so the fix screen reads
  // the same object the warning was built from rather than re-deriving it.
  canFix = false,
  onFix,
}) {
  const { gridRef, columns } = usePanelColumns();
  return (
    <div ref={gridRef} style={s.stack}>
      {/* Someone in transit between campuses. It sits at the TOP of this stack
          rather than in the page header: it is a fact about PEOPLE, and it
          belongs with the other people cards, not wedged between the title and
          the date picker where it pushed the whole chart down. TravelingBox
          renders null when nobody is travelling, so it costs no space the rest
          of the time. */}
      {travelingBox}

      {/* ⚠️ ABOVE THE GRID, NOT IN IT. This is a question about ROOMS that only
          appears when the board is unsure, and the grid below is measured — a
          card that comes and goes inside `auto-fit` reflows every other card in
          its row the moment a room finishes. Full width, directly under the
          travelling row, where the eye already lands for "what changed". */}
      {finishedRoomsCard}

      {/* At-a-glance state first — is anything wrong right now.
          ⚠️ `oldestEmergencyMin` USED TO BE MISSING FROM THIS LIST.
          TimelineChartPage computed it and nothing forwarded it, so the band
          fell back to its `null` default and printed "· age unknown" on EVERY
          emergency — including one sent four minutes earlier. That clause
          exists to separate a fresh alarm from one nobody closed last month,
          and unwired it said "unknown" to both. Nothing threw; the strip simply
          told a small lie. Check this destructure against the band's props
          before adding another. */}
      <TimelineStatusBand
        emergencies={status?.emergencies ?? 0}
        urgencies={status?.urgencies ?? 0}
        breaksWaiting={status?.breaksWaiting ?? 0}
        reliefInProgress={status?.reliefInProgress ?? 0}
        reliefRows={status?.reliefRows || []}
        oldestBreakWaitMin={status?.oldestBreakWaitMin ?? null}
        breakWaitingRows={status?.breakWaitingRows || []}
        emergencyRows={status?.emergencyRows || []}
        oldestEmergencyMin={status?.oldestEmergencyMin ?? null}
        onClearEmergency={onClearEmergency}
        onConfirmRelief={onConfirmRelief}
        busy={busy}
        alertsCovered={alertsCovered}
        stamp={checkedAtLabel ? `as of ${checkedAtLabel}` : null}
      />

      {/* ONE GRID FOR THE WHOLE PANEL — asked for 2026-09-06, and the reason is
          THE HOLE. This was three independent regions: a 2-up row, a flex row
          split 1:2, and a full-width footer. A SHORT card beside a TALL one
          therefore ended at its own content and left bare page showing through —
          NEEDS YOU NOW carrying one line next to a six-person FREE NOW opened a
          void most of a screen tall.
          A grid alone does not fix that. `alignItems: "start"` is what let a card
          stop above its row. Every card below is a grid item at its cell's FULL
          height (`stretch`, restated on the style so it cannot be lost), so the
          card's own surface covers the cell and the page never shows between two
          cards on one row.
          AUTO-PLACEMENT, NEVER EXPLICIT ROWS. `auto-fit` drops the grid to two
          tracks and then one as the panel narrows; an explicit `gridRow` would
          strand a card in a column that no longer exists. The wide cards use
          spans, which CLAMP to the track count, so they go full width on a
          phone instead of overflowing it. */}
      <div style={{ ...s.panelGrid, gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {/* ROW 1 — the shift read left to right: what needs you, who you ring
            about it, who is free to answer it. NEEDS YOU NOW and FREE NOW stay
            in the same row on purpose: the third card is the ANSWER to the
            first, and MD CALL sits between them because ringing a doctor is the
            other answer. */}
        {/* ORIENTEES RIDES WITH NEEDS YOU NOW rather than taking a cell of its
            own. On a normal day it is one line ("Nobody is orienting today") and
            NEEDS YOU NOW is one alert, so alone they measured 58px and 110px of
            content in cells 344px and 309px tall — 83% and 65% empty. Stacked
            they fill one track between them. Both are "who needs attention",
            which is why this pairing and not another. */}
        <div style={s.cellStack}>
          <NeedsYouNow
            items={outstanding}
            unavailable={!alertsCovered}
            checkedAtLabel={checkedAtLabel}
            canFix={canFix}
            onFix={onFix}
          />
          {/* The LAST card in a stack grows into whatever the row's tallest
              card left over — see `cellFill`. Without it the stack ends at its
              own content and the page shows through underneath, which is the
              original hole in a smaller box. */}
          <div style={s.cellFill}>
            <Orientees rows={orienteeRows} onMovePreceptorOut={onMovePreceptorOut} onSwapPair={onSwapPair} onEditPair={onEditPair} />
          </div>
        </div>
        <MdCall rows={mdCall} departed={departedDoctors} renderDepart={renderDepart} />
        <FreeNow
          providers={available?.providers || []}
          doctors={available?.doctors || []}
          freeSince={freeSince}
          nowMs={nowMs}
          nowMin={nowMin}
          canAssign={canAssign}
          onAssign={onAssign}
          renderSendHome={renderSendHome}
        />

        {/* ROW 2 — what is being worked, and what the day is heading into.
            ⚠️ BREAKS IS THE TALL CARD: one row per provider on the board, 16 on
            a full day, so it sets this row's height and BOUNDARY and MAINTENANCE
            stretch beside it. That is the intent — a tall white card beats bare
            page — but it is also why a `maxHeight` must never be added here
            without a scroll: every row in this card is tappable. */}
        {/* BREAKS TAKES TWO TRACKS AT THREE COLUMNS, so its rows reflow two-up
            (see TimelineBreaksCard's `list`) instead of running 491px down a
            single track and setting the height of everything beside it.
            AT TWO COLUMNS IT SPANS BOTH, and that is measured, not tidiness: in
            the page's 355px side-rail track its rows cannot reflow, so it went
            back to 505px and left FREE NOW beside it 210px empty. Spanning the
            row gives it 710px, the rows go two-up again, and the card that used
            to set the row height now matches it. At one column it is the row. */}
        <div style={columns >= 2 ? s.cellWide : s.cell}>
          {/* ⚠️ THE ROSTER FLAG, NOT THE BENCH. This used to filter the FREE NOW
              pool, which silently meant "break-givers who happen to be free" —
              so a CRNA holding a room could never appear here, and until
              2026-09-07 could not even be given the label. `available.breakGivers`
              reads the flag itself and carries each person's room, so the AIC
              can see which of them has to be pulled out of one.
              Roster-derived, not alert-derived. */}
          <TimelineBreaksCard
            rows={breakRows}
            breakGivers={available?.breakGivers || []}
            unavailable={!alertsCovered}
            onOpenRoom={onOpenRoom}
            onAssignBreak={onAssignBreak}
            onCancelRequest={onCancelRequest}
            busy={busy}
          />
        </div>
        {/* BOUNDARY + MAINTENANCE SHARE THE REMAINING TRACK. Measured alone
            beside BREAKS they held 100px and 119px of content in 505px cells.
            Stacked, they fill one track and the row stops being mostly paint.
            The wrapper is CONDITIONAL on the maintenance slot: it is opt-in, and
            a caller that does not pass it must get back no DOM at all, not an
            empty box holding its place (the doctrine the slots test spells
            out). */}
        {maintenanceCard ? (
          <div style={s.cellStack}>
            <BoundaryOutlook
              boundary={boundary}
              facilityLabel={facilityLabel}
              weekdayLabel={weekdayLabel}
            />
            <div style={s.cellFill}>{maintenanceCard}</div>
          </div>
        ) : (
          <BoundaryOutlook
            boundary={boundary}
            facilityLabel={facilityLabel}
            weekdayLabel={weekdayLabel}
          />
        )}

        {/* ROW 3 — the board's own outbox, full width. Its composer is three
            selects plus a message box; given one track of three it wrapped every
            control, and given two it still left the detail field stunted. It is
            the one card here that reads as a form rather than a list, so it gets
            the row. */}
        {taskCard ? <div style={s.cellFull}>{taskCard}</div> : null}

        {/* ROW 4 — the locums' leaving times, full width at the foot. Same
            people as MD CALL, but "set it and forget it" rather than something
            to scan, so it reads last. LocumList renders null when there are no
            locums, which leaves this cell empty and costs one 12px gap at the
            very bottom of the stack — deliberately not guarded, because a
            wrapper cannot know what its child will render. */}
        {locumList ? <div style={s.cellFull}>{locumList}</div> : null}
      </div>
    </div>
  );
}

const s = {
  stack: { display: "flex", flexDirection: "column", gap: 12, minWidth: 0 },
  // ONE GRID FOR EVERY CARD. auto-fit, so the track count is decided by the
  // width actually given — three on a board, two on a laptop's side panel, one
  // on a phone — rather than by a guessed breakpoint.
  panelGrid: {
    // gridTemplateColumns is set at render from the MEASURED width — see
    // usePanelColumns. `minmax(0, 1fr)` there, never a bare `1fr`: `1fr` is
    // `minmax(auto, 1fr)`, and an `auto` minimum lets one wide card (the task
    // composer's three selects) push the whole grid past its container.
    display: "grid",
    gap: 12,
    // ⚠️ DENSE, BECAUSE ONE CARD SPANS. At two columns BREAKS takes both tracks,
    // so it cannot start in column 2 — sparse packing would push it to the next
    // row and leave the cell beside FREE NOW empty, which is the hole again by
    // another route. Dense lets the single-track card that follows backfill it.
    // Inert at three columns and at one, where nothing is left behind.
    gridAutoFlow: "row dense",
    // ⚠️ STRETCH, AND IT IS LOAD-BEARING. Restated rather than left to the
    // default because `alignItems: "start"` is exactly what put the hole in the
    // old layout: a short card ended at its own content and bare page showed
    // through beside the tall card next to it. Setting this back to "start"
    // reopens it.
    alignItems: "stretch",
    minWidth: 0,
  },
  // A grid item that CONTAINS a card rather than being one. The node slots
  // (maintenance, task, locum) and TimelineBreaksCard own their own roots, and
  // this file must not reach into them to add a height. A one-item grid
  // stretches its child on both axes, so the card fills the cell with no style
  // change on its side at all.
  cell: { display: "grid", minWidth: 0 },
  // ⚠️ A SPAN DOES NOT CLAMP TO THE TRACKS THAT EXIST — IT CREATES MORE.
  // `gridColumn: "span 3"` here was measured, in the 646px side rail, producing
  // `280px 280px 62.0139px`: auto-fit had correctly chosen TWO tracks, and the
  // span opened a third IMPLICIT one that the row could not pay for, so the
  // right-hand card was sliced off the edge of the page. Nothing threw — grid
  // is happy to build a column it cannot fit.
  // `1 / -1` is line-based, not a span: it reaches the end of the EXPLICIT grid
  // and cannot invent a track, so it is full width at one, two or three columns.
  cellFull: { display: "grid", minWidth: 0, gridColumn: "1 / -1" },
  // TASK DIRECTIVES is the one card that wants two tracks rather than all of
  // them — its composer is three selects wide, and squeezed into a single
  // track every control wrapped.
  // ⚠️ MEASURED AT ONE TRACK BEFORE BEING LEFT LIKE THIS. The trap above is
  // about a span WIDER than the explicit grid; a `span 2` in a one-track grid
  // is the same shape of mistake, so it was checked at 375px rather than
  // reasoned about. Re-measure if this number ever changes.
  cellWide: { display: "grid", minWidth: 0, gridColumn: "span 2" },
  // TWO CARDS SHARING ONE TRACK. Not a nicety: measured, four cards in this
  // panel held 58-119px of content in cells 309-505px tall, because a row's
  // height is set by its tallest card and `stretch` then pays for the rest in
  // paint. Pairing the short cards is what actually removes the empty space;
  // stretching only changed its colour from page-grey to card-white.
  cellStack: { display: "flex", flexDirection: "column", gap: 12, minWidth: 0 },
  // The last card in a stack takes the leftover height. A stack of two cards at
  // their natural heights ends above its row exactly the way a single short card
  // did, and the page shows through underneath — the same hole, in a smaller
  // box. `display: grid` makes the child fill this box on both axes without
  // reaching into its styles.
  cellFill: { flex: 1, display: "grid", minWidth: 0 },
  // The ORIENTEES row is its OWN shape, not the shared `person` box: it carries
  // a second line, and `s.person` is used by three other cards that must not
  // grow one.
  orienteeRow: { display: "flex", flexDirection: "column", gap: 4, padding: "5px 7px", background: color.surface.base, border: `1px solid ${color.border.default}`, borderRadius: radius.sm },
  orienteeTop: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
  // Room + leaving hour, on their own line, indented past the avatar to line up
  // with the "with <name>" row below.
  orienteeFacts: { display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", paddingLeft: 32 },
  // ⚠️ THE NAME WRAPS, IT DOES NOT ELLIPSIS. This card exists to say WHO is
  // orienting; "Joshua Larso…" fails at the one job it has. Two lines of a real
  // name beat one line of a clipped one, and now that the name owns its row it
  // almost never needs the second.
  orienteeName: {
    fontSize: 12.5, fontWeight: font.weight.bold, color: color.text.primary,
    minWidth: 0, overflowWrap: "anywhere",
  },
  orienteeMeta: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", paddingLeft: 32 },
  orienteeWith: { fontSize: 11, color: color.text.muted, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  orienteeWithName: { color: color.text.secondary, fontWeight: font.weight.bold },
  // Its own line too, so the refusal reads as a sentence about the row rather
  // than a fragment wedged between a name and a button.
  orienteeBlocked: { fontSize: 10, color: color.text.muted, fontStyle: "italic", flexBasis: "100%" },
  // ⚠️ NO `marginLeft: auto` ON THE BUTTONS — `justifyContent` does the aligning.
  // Each carried one from when it was the lone control on a shared line. This
  // group is `flexBasis: 100%`, so it is a FULL-WIDTH line with real free space:
  // two auto margins would split that space between them and strand a gap of
  // ~50px between SWAP and Preceptor → FREE NOW, with SWAP floating mid-row. Auto
  // margins also nullify `justifyContent` outright (Flexbox §8.1), so the two
  // cannot be left to fight. Only visible on a row offering BOTH controls, which
  // today's board happens not to have — the reason this needed measuring, not
  // looking.
  //
  // `flexShrink: 0` for the same reason as everywhere else on this card: what
  // gives way under pressure has to be the "with <name>" text, not a control.
  // ⚠️ A CONTROL THAT LOOKS LIKE THE TEXT IT REPLACED. The name is the content
  // of this card, not a button in it — restyling it as a chip would turn a
  // roster into a toolbar. A dotted underline is the whole affordance.
  //
  // ⚠️ THE 24px TARGET (SC 2.5.8) COMES FROM THIS PADDING, NOT FROM THE ROW.
  // An earlier comment here claimed the row's line height supplied it; the line
  // box is 12.5 × 1.3 ≈ 16.25px, nowhere near 24, and the control was passing
  // only on the SPACING exception — by a couple of px, on an unpinned
  // line-height. `4px 0` makes the bounding box 24.25px on its own. Vertical
  // padding only, so the name still reads as text, and the row does not grow
  // because the avatar beside it is already 24px.
  //
  // ⚠️ `textDecoration`, NOT `borderBottom`. As a flex item this box is
  // BLOCKIFIED whatever `display` says, and a border draws once under the whole
  // box — so a name that wrapped to two lines got one rule under the second
  // line and nothing under the first. An underline follows the text.
  //
  // `display: inline` is kept for the day this style is reused outside a flex
  // row; a flex box will not wrap its own text, which is what quietly turned
  // the fix for the truncation back into a truncation.
  orienteeNameBtn: {
    font: "inherit", fontSize: 12.5, fontWeight: 700, color: color.text.primary,
    background: "none", border: 0,
    textDecoration: "underline dotted", textUnderlineOffset: 2,
    padding: "4px 0", lineHeight: 1.3, margin: 0, cursor: "pointer", textAlign: "left",
    display: "inline", minWidth: 0, overflowWrap: "anywhere",
  },
  // Line 2's preceptor name. This one KEEPS the single-line truncation, because
  // it sits inside `orienteeWith`, which is itself an ellipsis row — a wrapping
  // child inside a nowrap parent is clipped, not wrapped.
  // This one conforms to SC 2.5.8 through the INLINE exception — it sits inside
  // the sentence "with <name>", with non-target text on the same line — so it
  // needs no padding of its own. Same underline treatment for consistency.
  orienteeWithBtn: {
    font: "inherit", fontSize: 11, fontWeight: font.weight.bold, color: color.text.secondary,
    background: "none", border: 0,
    textDecoration: "underline dotted", textUnderlineOffset: 2,
    padding: 0, margin: 0, cursor: "pointer", textAlign: "left",
    whiteSpace: "nowrap",
  },
  // ⚠️ ITS OWN LINE, ALWAYS. Owner, 2026-09-10: the controls "need be inside each
  // box". Sharing a line with "with <name>" put the button hard against the box
  // border and made the name the only thing that could give way — and the two
  // rows disagreed with each other, because a row with a refusal sentence
  // wrapped and a row without one did not. `flexBasis: 100%` takes the full
  // width of the wrapping row, so the group lands under the names on EVERY row
  // and the name never competes with a control for space.
  orienteeActions: {
    flexBasis: "100%", display: "flex", alignItems: "center", justifyContent: "flex-end",
    gap: 6, flexShrink: 0,
  },
  orienteeBtn: {
    // The same 24px minimum as `fixBtn` — WCAG 2.2 SC 2.5.8.
    minHeight: 24, minWidth: 24,
    display: "inline-flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box",
    fontSize: 10, fontWeight: 800, letterSpacing: ".5px", fontFamily: "inherit", lineHeight: 1,
    color: color.accent.primary, background: color.surface.card,
    border: `1px solid ${color.accent.primary}`, borderRadius: radius.sm,
    padding: "5px 9px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
  },
  // ⚠️ NOT COLOUR ALONE. The withheld state is carried by the `disabled`
  // attribute and by the reason printed beside it, so it survives a monochrome
  // screen and a screen reader.
  orienteeBtnOff: {
    minHeight: 24, minWidth: 24,
    display: "inline-flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box",
    fontSize: 10, fontWeight: 800, letterSpacing: ".5px", fontFamily: "inherit", lineHeight: 1,
    color: color.text.muted, background: color.surface.base,
    border: `1px solid ${color.border.default}`, borderRadius: radius.sm,
    padding: "5px 9px", cursor: "not-allowed", whiteSpace: "nowrap", flexShrink: 0,
    // ⚠️ index.css gates its hover brightness on `button:not(:disabled)`. Now
    // that this button is only `aria-disabled`, it would light up on hover like
    // a live control. Say plainly that it does not react.
    filter: "none",
  },
  orienteeRoom: { fontSize: 11, fontWeight: font.weight.bold, color: color.text.secondary, whiteSpace: "nowrap" },
  orienteeLeave: { fontSize: 11, color: color.text.muted, whiteSpace: "nowrap" },

  card: { background: color.surface.card, border: `1px solid ${color.border.default}`, borderRadius: radius.lg, padding: "11px 13px 13px", minWidth: 0 },
  cardHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, marginBottom: 9, paddingBottom: 7, borderBottom: `1px solid ${color.border.default}` },
  cardTitle: { fontSize: 11, fontWeight: font.weight.bold, letterSpacing: ".9px", color: color.text.secondary, margin: 0, display: "flex", alignItems: "center", gap: 7 },
  cardCount: { fontSize: 10, fontWeight: font.weight.bold, background: color.surface.raised, color: color.text.secondary, borderRadius: radius.full, padding: "1px 7px", letterSpacing: 0 },
  cardNote: { fontSize: 10.5, color: color.text.muted, letterSpacing: ".3px", whiteSpace: "nowrap" },

  rows: { display: "flex", flexDirection: "column", gap: 7 },
  row: { display: "flex", alignItems: "flex-start", gap: 9, padding: "8px 10px", border: `1px solid ${color.border.default}`, borderLeftStyle: "solid", borderRadius: radius.sm },
  // ⚠️ THE SEVERITY WORD WAS THE SMALLEST TEXT ON THE PANEL. Measured at 8.5px
  // while the alert it labels ran at 13.5px and ordinary body text at 12px —
  // so ALARM / ACT NOW / PLAN, the one word that says how much this matters,
  // was the hardest thing on the card to read. Visual hierarchy that runs
  // backwards is a hierarchy that lies. 11px, above every other label here.
  tier: { fontSize: 11, fontWeight: 800, letterSpacing: ".6px", border: "1px solid", borderRadius: radius.full, padding: "2px 6px", whiteSpace: "nowrap", flexShrink: 0, marginTop: 1 },
  rowBody: { minWidth: 0, flex: 1 },
  rowTitle: { fontWeight: font.weight.bold, color: color.text.primary, lineHeight: 1.3 },
  rowRooms: { fontSize: 10.5, color: color.text.secondary, marginTop: 2, lineHeight: 1.35 },
  // Sized to sit INSIDE the existing row box: 22px tall against a title line of
  // 16-20px plus the rooms line, so it adds no height. `flexShrink: 0` keeps it
  // off the title when a long label wraps.
  fixBtn: {
    // WCAG 2.2 SC 2.5.8: a target is at least 24x24 CSS px. It measured 22px tall.
    // inline-flex centring keeps the label where it was — the box grows,
    // the type does not move.
    minHeight: 24,
    minWidth: 24,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: ".5px",
    fontFamily: "inherit",
    lineHeight: 1,
    color: color.accent.primary,
    background: color.surface.card,
    border: `1px solid ${color.accent.primary}`,
    borderRadius: radius.sm,
    padding: "5px 9px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
    marginTop: 1,
  },

  allClear: { background: "#e6f8f3", border: `1px solid ${color.accent.active}`, borderRadius: radius.sm, padding: "10px 11px" },
  allClearTitle: { fontSize: 12.5, color: "#096e53", display: "block" },
  allClearLabel: { fontSize: 10.5, fontWeight: font.weight.bold, letterSpacing: ".7px", color: color.text.muted, marginTop: 7 },
  allClearList: { margin: "3px 0 0", paddingLeft: 16, fontSize: 10.5, color: color.text.secondary, lineHeight: 1.5 },

  unavailable: { background: "#fef8ec", border: `1px solid ${color.accent.closing}`, borderRadius: radius.sm, padding: "10px 11px" },
  unavailableTitle: { fontSize: 12, color: "#7a4f00", display: "block" },
  unavailableBody: { fontSize: 10.5, color: "#7a4f00", lineHeight: 1.45, margin: "4px 0 0" },

  people: { display: "flex", flexDirection: "column", gap: 5 },
  person: { display: "flex", alignItems: "center", gap: 8, padding: "5px 7px", background: color.surface.base, border: `1px solid ${color.border.default}`, borderRadius: radius.sm },
  // ⚠️ QUIETER, NOT ALARMING. Somebody past their shift end is a housekeeping
  // question, not an emergency — red on this board belongs to the rooms that are
  // in trouble. A dashed border and a faded ground say "still here, probably
  // shouldn't be" without competing with a real alert.
  personDueOut: {
    display: "flex", alignItems: "center", gap: 8, padding: "5px 7px",
    background: color.surface.card, border: `1px dashed ${color.border.default}`,
    borderRadius: radius.sm, opacity: 0.85,
  },
  dueOutHead: {
    display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap",
    marginTop: 4, paddingTop: 6, borderTop: `1px solid ${color.border.default}`,
    fontSize: 10, fontWeight: 800, letterSpacing: ".5px", color: color.text.muted,
  },
  dueOutNote: { fontWeight: 400, letterSpacing: 0, fontSize: 10, color: color.text.muted },
  // The hour itself carries the meaning, so it is text — never a colour alone.
  dueOutTag: { fontSize: 10, fontWeight: 700, color: color.text.secondary, whiteSpace: "nowrap" },
  // The name on its own line, everything secondary on a second one beneath it.
  // `minWidth: 0` is load-bearing: without it this column's min-content width is
  // the longest name, which would push the row wider than the card instead of
  // letting the name have the space.
  personBody: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 },
  // Chips, role and the free-since time. Wraps rather than squeezing the line
  // above it, because none of these is the answer to "who is free".
  personSub: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, minWidth: 0 },
  avatar: { width: 24, height: 24, borderRadius: radius.full, background: "#e6f8f3", color: "#096e53", fontSize: 10.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  // Name left, hour right, on one line. The name still owns the shrinking:
  // `minWidth: 0` on it and `flexShrink: 0` on the hour, because a truncated
  // clock time ("3 P…") is worse than useless.
  personNameRow: { display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 },
  personDepart: { marginLeft: "auto", flexShrink: 0, fontSize: 11, fontWeight: font.weight.bold, color: color.text.muted, whiteSpace: "nowrap" },
  // Past their hour: the same number, said louder. Not red — this is
  // housekeeping, and red on this board belongs to the rooms in trouble.
  personDepartOut: { marginLeft: "auto", flexShrink: 0, fontSize: 11, fontWeight: 800, color: color.text.secondary, whiteSpace: "nowrap" },
  personName: { fontSize: 12.5, fontWeight: font.weight.bold, color: color.text.primary, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  personMeta: { fontSize: 10, color: color.text.muted, whiteSpace: "nowrap" },
  // Teal, outlined, no fill — the same family `SHIFT_COLOR_ON_CALL` (#0f766e,
  // timeline.js) already uses for "on call" wherever a shift is labelled, so the
  // two surfaces read as one idea. Outlined rather than filled because every
  // filled colour on this stack means somebody is in trouble. #0f766e on white
  // is 5.2:1, which a 9px chip needs.
  onCallChip: {
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: ".5px",
    color: "#0f766e",
    border: "1px solid #0f766e",
    borderRadius: radius.full,
    padding: "1px 6px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  // Same shape as OB CALL, different hue: the two chips are the same KIND of
  // fact (why this free person is not spare), so they must not look like
  // different kinds of thing. #7a4f00 is the readable ink for the board's break
  // amber — the raw #f59e0b is 2.15:1 on white and fails AA outright.
  givingBreaksChip: {
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: ".5px",
    color: "#7a4f00",
    // The chip's border is the only thing marking where the chip is, so
    // WCAG 1.4.11 wants 3:1. Raw #f59e0b is 1.84:1 on this ground and fails.
    // #b45309 is the same amber family, darker: 5.02:1 on card, 4.31:1 on base.
    border: "1px solid #b45309",
    borderRadius: radius.full,
    padding: "1px 6px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  // The duration is the point of the row, so it gets weight and a monospace
  // figure so digits do not jitter as the minutes tick.
  // A ROW, AND NO `marginLeft: auto`. It used to be a right-pinned column on
  // the name's own line, which is what pushed the name into an ellipsis.
  freeBlock: { display: "flex", alignItems: "baseline", gap: 5, lineHeight: 1.15 },
  freeAt: {
    fontSize: 12,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    whiteSpace: "nowrap",
  },
  // Monospace so the digits do not jitter as the minutes tick.
  freeFor: {
    fontSize: 10,
    color: color.text.muted,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    whiteSpace: "nowrap",
  },
  // TBD is deliberately QUIET. It is not a warning — it means this provider was
  // never in a room today, which is ordinary at the start of a shift.
  srOnly: {
    position: "absolute", width: 1, height: 1, padding: 0, margin: -1,
    overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0,
  },
  tbd: {
    fontSize: 10,
    fontWeight: font.weight.bold,
    letterSpacing: ".4px",
    color: color.text.muted,
  },
  assignBtn: {
    // WCAG 2.2 SC 2.5.8: a target is at least 24x24 CSS px. It measured 23px tall.
    // inline-flex centring keeps the label where it was — the box grows,
    // the type does not move.
    minHeight: 24,
    minWidth: 24,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    fontSize: 11,
    fontWeight: font.weight.bold,
    fontFamily: "inherit",
    background: color.accent.primary,
    color: "#fff",
    border: "none",
    borderRadius: radius.sm,
    padding: "5px 9px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    // Never squeezed, and never the thing that squeezes the name: it sits
    // outside `personBody`, which absorbs the width instead.
    flexShrink: 0,
  },

  splitRow: { display: "flex", gap: 11, alignItems: "flex-start", paddingTop: 9, marginTop: 9, borderTop: `1px solid ${color.border.default}` },
  splitLabel: { fontSize: 10.5, fontWeight: font.weight.bold, letterSpacing: ".6px", color: color.text.muted, width: 74, flexShrink: 0, lineHeight: 1.35 },
  splitNote: { fontSize: 10.5, color: color.text.secondary, marginTop: 4, lineHeight: 1.4 },

  // LOUD. Staffing is allowed to raise its voice.
  staffChip: { display: "inline-block", fontSize: 12, fontWeight: 800, letterSpacing: ".4px", padding: "5px 9px", borderRadius: radius.sm, border: "2px solid" },
  staffChipShort: { background: "#fef8ec", borderColor: color.accent.closing, color: "#7a4f00" },
  staffChipOk: { background: color.surface.base, borderColor: color.border.default, color: color.text.secondary },

  // QUIET. Flat, grey, monospaced — and identical whether under, at or over.
  contractReadout: { display: "inline-block", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, color: color.text.secondary, background: color.surface.base, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "5px 9px" },
  sourceNote: { fontSize: 10.5, color: color.text.muted, marginTop: 9, lineHeight: 1.35 },

  emptyLine: { fontSize: 11, color: color.text.muted, fontStyle: "italic" },

  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", fontSize: 10, fontWeight: font.weight.bold, letterSpacing: ".7px", color: color.text.muted, padding: "0 0 5px" },
  td: { fontSize: 12.5, color: color.text.primary, padding: "7px 0", borderTop: `1px solid ${color.border.default}`, verticalAlign: "middle" },
  // ⚠️ THE POSITION COLUMN MUST SHRINK TO ITS BADGE. A `width: 100%` table in
  // `auto` layout hands the leftover space to the columns in proportion, not to
  // the one that needs it: measured, this column was **94px wide around a 20px
  // badge**, so the call number floated ~70px clear of the name it belongs to
  // and the card read as two unrelated stripes. `width: 1%` + `nowrap` is the
  // shrink-to-fit idiom — the column takes its content and the doctor's name
  // takes everything else.
  thTight: { width: "1%", whiteSpace: "nowrap", paddingRight: 12 },
  callNum: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: radius.full, background: color.accent.primary, color: "#fff", fontSize: 10, fontWeight: 800 },
  locumPill: { display: "inline-block", fontSize: 10.5, fontWeight: font.weight.bold, color: "#7b2fbe", border: "1px solid #a855f7", borderRadius: radius.full, padding: "1px 7px" },
  mdRow: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", minWidth: 0 },
  mdNameDeparted: { textDecoration: "line-through", color: color.text.muted },
  departedTag: { fontSize: 10.5, fontWeight: font.weight.bold, letterSpacing: ".4px", color: color.text.muted, textTransform: "uppercase" },
  mdAction: { marginLeft: "auto" },
  leaveTag: { fontSize: 10.5, color: color.text.muted, marginLeft: 7, border: `1px solid ${color.border.default}`, borderRadius: radius.full, padding: "1px 6px" },
};
